import React, { ReactNode, useId } from "react";

import { ResilienceReport } from "apps/company/api/rest/company-dashboard-resilience";
import { useTranslation } from "lib/i18n/use-translation";

import { K, NotEnoughData, useDomainName } from "./parts";
import { band, Domain, formatScore } from "./resilience-view";
import styles from "./resilience.module.scss";

const RankedCard = ({
  title,
  description,
  badge,
  children,
}: {
  title: string;
  description: string;
  badge: string;
  children: ReactNode;
}) => {
  const headingId = useId();
  return (
    <section className={styles["card"]} aria-labelledby={headingId}>
      <div className={styles["ranked-head"]}>
        <h3 id={headingId} className={styles["card-title"]}>
          {title}
        </h3>
        <span className={styles["badge"]}>{badge}</span>
      </div>
      <p className={styles["card-description"]}>{description}</p>
      {children}
    </section>
  );
};

const Ranked = ({
  domains,
  report,
  marked,
}: {
  domains: string[];
  report: ResilienceReport;
  marked: boolean;
}) => {
  const { i18n } = useTranslation(["admin"]);
  const domainName = useDomainName();
  if (domains.length === 0) return <NotEnoughData />;
  const tiles = report.scores.selected.all_staff.domains;
  return (
    <ol className={styles["ranked"]}>
      {domains.map((domain) => {
        const tile = tiles[domain];
        if (tile.suppressed) {
          throw new Error(`A hidden domain was ranked: ${domain}`);
        }
        const steady = marked && band(tile.value) === "steady";
        return (
          <li key={domain} className={styles["ranked-item"]}>
            <span className={styles["ranked-name"]}>
              {domainName(domain as Domain)}
              {steady && (
                <span
                  className={styles["marker"]}
                  data-testid="strength-marker"
                  aria-hidden="true"
                >
                  *
                </span>
              )}
            </span>
            <strong className={styles["ranked-score"]}>
              {formatScore(tile.value, i18n.language)}
            </strong>
          </li>
        );
      })}
    </ol>
  );
};

const StrengthsFeedback = ({ report }: { report: ResilienceReport }) => {
  const { t } = useTranslation(["admin"]);
  const selected = report.scores.selected;
  const tiles = selected.all_staff.domains;
  const anySteady = selected.strengths.some(
    (domain) =>
      !tiles[domain].suppressed && band(tiles[domain].value) === "steady"
  );

  return (
    <div className={styles["panels"]}>
      <RankedCard
        title={t(`${K}.strengthsFeedback.strengths`)}
        description={t(`${K}.strengthsFeedback.strengthsDescription`)}
        badge={t(`${K}.strengthsFeedback.strengthsBadge`)}
      >
        <Ranked domains={selected.strengths} report={report} marked />
        {anySteady && (
          <p className={styles["footnote"]}>
            {t(`${K}.strengthsFeedback.steadyFootnote`)}
          </p>
        )}
      </RankedCard>
      <RankedCard
        title={t(`${K}.strengthsFeedback.focusAreas`)}
        description={t(`${K}.strengthsFeedback.focusAreasDescription`)}
        badge={t(`${K}.strengthsFeedback.focusAreasBadge`)}
      >
        <Ranked domains={selected.focus_areas} report={report} marked={false} />
      </RankedCard>
    </div>
  );
};

export default StrengthsFeedback;
